type State = { token: string | null; user: { sub: string; role?: string } | null };
export type OrganizationGpuSessionStore = {
  getState(): State;
  subscribe(listener: (state: State) => void): () => void;
};
/** Bind remote generation to its initiating account; no automatic retry under a replacement session. */
export async function withGenerationSession<T>(
  store: OrganizationGpuSessionStore,
  owner: string,
  signal: AbortSignal | undefined,
  work: (config: {
    headers: { Authorization: string };
    _retry: true;
    signal: AbortSignal;
  }) => Promise<T>,
  allowedRoles?: readonly string[],
): Promise<T> {
  const initial = store.getState();
  if (
    !initial.token ||
    initial.user?.sub !== owner ||
    (allowedRoles !== undefined && !allowedRoles.includes(initial.user?.role ?? ""))
  )
    throw new Error("GPU 생성 권한 또는 로그인 계정을 확인해 주세요.");
  const token = initial.token,
    role = initial.user?.role;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) abort();
  const unsubscribe = store.subscribe((state) => {
    if (state.token !== token || state.user?.sub !== owner || state.user.role !== role) abort();
  });
  try {
    if (controller.signal.aborted) throw new Error("요청이 취소되었거나 계정이 변경되었습니다.");
    const result = await work({
      headers: { Authorization: `Bearer ${token}` },
      _retry: true,
      signal: controller.signal,
    });
    if (controller.signal.aborted) throw new Error("요청이 취소되었거나 계정이 변경되었습니다.");
    return result;
  } finally {
    unsubscribe();
    signal?.removeEventListener("abort", abort);
  }
}

/** Organization membership is checked again by the server; Pro permits all authenticated roles. */
export function withOrganizationGpuSession<T>(
  store: OrganizationGpuSessionStore,
  owner: string,
  signal: AbortSignal | undefined,
  work: Parameters<typeof withGenerationSession<T>>[3],
): Promise<T> {
  return withGenerationSession(store, owner, signal, work, [
    "HOSPITAL_ADMIN",
    "TEAM_ADMIN",
    "SUPER_ADMIN",
  ]);
}
